import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import "./sessionClosingDrawer.css";
import ClosingHoldModal from "../../../product/all-products/components/ClosingHoldModal/ClosingHoldModal";
import SessionPageLeft from "../../../product/sessionPageV2/SessionPageLeft";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LogoutIcon from "@mui/icons-material/Logout";
import HistoryIcon from "@mui/icons-material/History";
import PreviousSessionMessage from "../../../product/sessionPage/components/SessionListing/components/previousSession";
import { PosCustomButton } from "../../../../../customComponents/PosCustomButton/PosCustomButton";
import PosCustomIcon from "../../../../../customComponents/PosCustomIcon/PosCustomIcon";
import { ArrowLongRightIcon, LockClosedIcon } from "@heroicons/react/24/solid";
import SummaryCard from "../../../product/sessionPage/components/SessionListing/components/SummaryCard/SummaryCard";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { useSnackbar } from "../../../product/hooks/SnackbarProvider";
import useGetDevice from "../../../product/hooks/useGetDevice";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getAllSessionLicenseActive,
  getAllUserDetails,
  getSessionCloseDetails,
  getSessionData,
  holdCartList,
  holdCartListSession,
} from "../../../../../config/Services";
import { customEventTrigger } from "../../../../../analytics";
import { addmanagerpin, addpin } from "../../../product/all-products/redux/Actions/SessionActions";
import { format } from "date-fns";
import { convertToLocalTime } from "../../../product/all-products/Middleware/commonUtils";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ClockIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import ChefpngIcon from "../../../../../assets/images/chef-png-icon.png";
import { UserContext } from "../../../../../App";
import CustomerPaginatedTable from "../../../product/all-products/CustomerV2Preview/Component/CustomComponent/CustomerPaginatedTable";

// --------------------------------- custome batch for reusability

const PosCustomStatusBatch = ({ status = "ACTIVE", customLabel, padding }) => {
  const STATUS_CONFIG = {
    ACTIVE: { bg: "#BFE6A3", color: "#095120", dot: "#095120", label: "Active" },
    OPEN: { bg: "#e3f2fd", color: "#1565c0", dot: "#1565c0", label: "Open" },
    CLOSED: { bg: "#FEF3F2", color: "#E90000", dot: "#E90000", label: "Closed" },
    PENDING: { bg: "#fff8e1", color: "#e65100", dot: "#e65100", label: "Pending" },
    ONGOING: { bg: "#f3e5f5", color: "#6a1b9a", dot: "#6a1b9a", label: "Ongoing" },
  };

  const key = status?.toString().toUpperCase();
  const config = STATUS_CONFIG[key] ?? {
    bg: "#f5f5f5",
    color: "#616161",
    dot: "#616161",
    label: status,
  };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        backgroundColor: config.bg,
        color: config.color,
        borderRadius: "20px",
        padding: padding ?? "4px 10px", // ← custom padding, fallback to default
        fontSize: "0.75rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        userSelect: "none",
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          backgroundColor: config.dot,
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      {customLabel ?? config.label}
    </span>
  );
};

function getSessionDuration(startTime) {
  if (!startTime) return "00h 00m";

  const start = new Date(startTime);
  const now = new Date();

  const diffMs = now - start;

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const formattedHours = String(hours).padStart(2, "0");
  const formattedMinutes = String(minutes).padStart(2, "0");

  return `${formattedHours}h ${formattedMinutes}m`;
}

const SessionClosingDrawer = ({ navigate, open, onClose }) => {
  const showSnackbar = useSnackbar();
  const isMobile = useGetDevice();
  const navigateToPath = useNavigate();
  const dispatch = useDispatch();

  // Get session and organization info from localStorage
  const orgId = localStorage.getItem("orgId");
  const locId = localStorage.getItem("locId");
  const sessionId = localStorage.getItem("sessionId");
  const licenseId = localStorage.getItem("licenseId");
  const mode = localStorage.getItem("mode");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openHoldModal, setOpenHoldModal] = useState(false);
  const [holdCartCount, setHoldCartCount] = useState(null);
  const [accordionExpanded, setAccordionExpanded] = useState(false);

  const [isSessionLoading, setIsSessionLoading] = useState(false);

  const retailType = localStorage.getItem("retailType");

  const branchSubType = localStorage.getItem("branchSubType");

  const licenseName = localStorage.getItem("licenseName");

  const { saleMode } = useContext(UserContext);

  useEffect(() => {
    if (!open) {
      setDrawerOpen(false);
      setAccordionExpanded(false);
    }
  }, [open]);

  // Mutation to check for hold carts before stopping the session
  const { isLoading: isHoldCartLoading, mutate: checkHoldCartList } = useMutation({
    mutationFn: async (data) => {
      const response = await holdCartListSession(data);
      return response?.data?.data;
    },
    onSuccess: (data) => {
      if (!!data?.length) {
        setOpenHoldModal(true);
      } else {
        // navigateToPath("/session-page/denominations");
        // navigate("COD_SETTLEMENT_PAGE");
        navigate("DENOMINATION_PAGE");
      }
    },
    onError: (error) => {
      showSnackbar(error?.response?.data?.message || error?.message, "error");
    },
  });

  /**
   * Handles the stop session workflow.
   * Checks for hold carts, triggers analytics, and resets session pins in Redux.
   */

  const holdCartCheckFunction = () => {
    if (holdCartCount > 0) {
      setOpenHoldModal(true);
      return;
    }
    handleStopDay();
  };

  const handleStopDay = () => {
    // onClose();
    const payload = {
      sourceLocationId: locId,
      destinationId: locId,
      loggedInUser: uidx,
    };
    customEventTrigger("pos_stop_session_button", {
      pos_user_id: payload?.loggedInUser,
    });
    checkHoldCartList(payload);
    dispatch(addpin(""));
    dispatch(addmanagerpin(""));

    // internal navigating with in the drawer
    navigate("DENOMINATION_PAGE");
  };

  const { data: summaryData, isLoading: isSummaryLoading } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["sessionCloseDetails"],
    queryFn: async () => {
      const res = await getSessionCloseDetails(locId, licenseId, sessionId);
      if (res?.data?.code === "ECONNRESET") {
        throw new Error(res?.data?.message);
      }
      if (res?.data?.data?.es) {
        throw new Error(res?.data?.data?.message);
      }
      return res?.data?.data?.sessionData?.sessionSalesData;
    },
    onError: (err) => {
      showSnackbar(err?.message || err?.response?.data?.message, "error");
    },
  });

  // Fetch session data on mount
  useEffect(() => {
    fetchSessionData();
  }, []);

  const toggleDrawer = (open) => (event) => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
      return;
    }
    setDrawerOpen(open);
  };

  /**
   * Fetches session data for the current day.
   */
  const fetchSessionData = () => {
    const currentDate = format(new Date(), "yyyy-MM-dd");

    const payload = {
      pageNumber: 0,
      pageSize: 10,
      sortOrder: "ASCENDING",
      organizationId: orgId,
      locationId: locId,
      startDate: currentDate,
      endDate: currentDate,
    };
    getSessionData(payload)
      .then((res) => {
        if (res?.data?.es) {
          throw new Error(res?.data?.message);
        }
        showSnackbar(res?.data?.message, "success");
      })
      .catch((err) => {
        showSnackbar(err?.response?.data?.message || err?.message, "error");
      });
  };

  // to get all session active for particular licenseId

  const [sessionTable, setSessionTable] = useState([]);
  const [previousDay, setPreviousDay] = useState(false);
  const retailUserDetails = JSON.parse(localStorage.getItem("retailUserDetails"));
  const uidx = retailUserDetails?.uidx;

  // Separate active and inactive sessions
  const activeSessions = sessionTable?.filter((session) => session?.status === "ACTIVE") || [];
  const inactiveSessions = sessionTable?.filter((session) => session?.status !== "ACTIVE") || [];

  // Loader stays up until both the session table and the summary (orders) data are ready
  const isModalLoading = isSessionLoading || isSummaryLoading;

  useEffect(() => {
    if (licenseId) {
      setIsSessionLoading(true);

      const payload = {
        locationId: [locId],
        cartStatus: ["HOLD"],
        terminalName: [licenseName],
      };

      Promise.all([holdCartList(payload), getAllSessionLicenseActive(licenseId)])
        .then(([holdCartResponse, sessionRes]) => {
          // ---- handle hold cart response ----
          const holdCarts = holdCartResponse?.data?.data;

          setHoldCartCount(holdCarts?.currentPageResult || 0);
          // if (holdCarts?.currentPageResult > 0) {
          //   setOpenHoldModal(false);
          // }

          // ---- handle session license response (same logic as before) ----
          const res = sessionRes;
          if (res?.data?.message === "USER_SESSION_DO_NOT_EXIST" && res?.data?.es == 1) {
            setPreviousDay(true);
            return;
          }

          if (res?.data?.sessions) {
            setPreviousDay(false);
            const rawSessions = res?.data?.sessions;
            const uidxList = rawSessions?.map((item) => item?.uidx);

            const applyNames = (nameData) => {
              const sessionsWithNames = rawSessions?.map((item, i) => ({
                ...item,
                name: nameData?.[i]?.firstName || item?.name || "-",
              }));
              const sorted = sessionsWithNames?.sort((a, b) => {
                if (a?.uidx === uidx) return -1;
                if (b?.uidx === uidx) return 1;
                return 0;
              });
              setSessionTable(sorted);
            };

            return getAllUserDetails({ uidx: uidxList })
              .then((resp) => applyNames(resp?.data?.data))
              .catch(() => applyNames(null)); // Bug 2 fix: fall back to raw sessions if user-details call fails
          } else {
            // Bug 3 fix: unexpected response shape — leave table empty but clear previousDay so the
            // "No active sessions" row is shown rather than a blank screen.
            setPreviousDay(false);
          }
        })
        .catch((err) => {
          showSnackbar(err?.response?.data?.message || err?.message, "error");
        })
        .finally(() => {
          setIsSessionLoading(false);
        });
    }
  }, [licenseId, uidx]);

  /**
   * Navigates to the cart page.
   * Triggers analytics event and routes based on device type and retail type.
   */
  const goToCart = () => {
    customEventTrigger("pos_go_to_cart");

    if (retailType === "RESTAURANT") {
      if (branchSubType === "FINE_DINE" && saleMode === "DINE_IN") {
        navigateToPath("/products/table-layout");
      } else {
        navigateToPath("/restaurant/cart");
      }
    } else {
      // Grocery or other retail types
      navigateToPath("/grocery/cart");
    }
  };

  const tableWrapperStyle = {
    maxHeight: "300px",
    overflowY: "auto",
    overflowX: "hidden",
  };

  const activeUserRowStyle = {
    backgroundColor: "#e3f2fd",
    fontWeight: 600,
  };

  // Render session table rows
  const renderSessionRows = (sessions) => {
    return sessions?.map((item, i) => {
      return (
        <tr key={i} style={item?.uidx === uidx ? activeUserRowStyle : {}}>
          <td className="table-data-pos">{item?.name || "-"}</td>
          <td className="table-data-pos">{item?.licenseName || "-"}</td>
          <td className="table-data-pos">{item?.licenseId || "-"}</td>
          <td className="table-data-pos">{convertToLocalTime(item?.startTime)}</td>
          <td className="table-data-pos">
            {item?.endTime ? convertToLocalTime(item?.endTime) : "Ongoing"}
          </td>
          <td className={item?.status === "CLOSED" ? "table-data-posRed" : "table-data-posGreen"}>
            {item?.status || "-"}
          </td>
        </tr>
      );
    });
  };

  // AFTER — only recalculates when activeSessions or summaryData changes
  const currentSesionSummary = useMemo(
    () => [
      {
        id: "LOGIN_TIME",
        label: "LOGIN TIME",
        value: activeSessions?.[0]?.startTime
          ? convertToLocalTime(activeSessions?.[0]?.startTime)
          : "--:--",
        icon: <HistoryIcon sx={{ fontSize: 22, color: "#2A71FF" }} />,
        iconParentBg: "#EDF8FF",
      },
      {
        id: "TOTAL_DURATION",
        label: "TOTAL DURATION",
        value: getSessionDuration(activeSessions?.[0]?.startTime),
        icon: <ClockIcon height={22} width={22} color="#7C3AED" />,
        iconParentBg: "#F4EBFF",
      },
      {
        id: "ORDERS_PROCESSED",
        label: "ORDERS PROCESSED",
        value: summaryData?.totalOrders ?? "0",
        icon: <CheckCircleIcon height={22} width={22} color="#219647" />,
        iconParentBg: "#ECFDF3",
      },
      {
        id: "LIVE_ORDERS",
        label: "LIVE ORDERS",
        value: +(summaryData?.swiggyOrders ?? "0") + +(summaryData?.zomatoOrders ?? "0"),
        icon: <PosCustomIcon image={ChefpngIcon} height={"22px"} width={"22px"} color="#FFA500" />,
        iconParentBg: "#FFF6E5",
      },
    ],
    [activeSessions, summaryData]
  );

  return (
    // <Drawer
    //   anchor="right"
    //   open={open}
    //   onClose={onClose}
    //   PaperProps={{ className: "session-drawer-paper" }}
    // >
    // <section className="session_closing_home_page-container">
    //   <style>
    //     {`
    //       .table-scroll-wrapper::-webkit-scrollbar {
    //         width: 8px;
    //       }

    //       .table-scroll-wrapper::-webkit-scrollbar-track {
    //         background: #f1f1f1;
    //         border-radius: 4px;
    //       }

    //       .table-scroll-wrapper::-webkit-scrollbar-thumb {
    //         background: #888;
    //         border-radius: 4px;
    //       }

    //       .table-scroll-wrapper::-webkit-scrollbar-thumb:hover {
    //         background: #555;
    //       }

    //       .session-accordion {
    //         margin-top: 12px;
    //         box-shadow: none;
    //         border: 1px solid #e0e0e0;
    //         border-radius: 4px !important;
    //       }

    //       .session-accordion:before {
    //         display: none;
    //       }

    //       .accordion-summary {
    //         background-color: #f5f5f5;
    //         min-height: 48px !important;
    //       }

    //       .accordion-summary .MuiAccordionSummary-content {
    //         margin: 8px 0;
    //       }
    //     `}
    //   </style>

    //   <Modal
    //     open={openHoldModal}
    //     aria-labelledby="modal-modal-title"
    //     aria-describedby="modal-modal-description"
    //     className="modal-popup"
    //   >
    //     <ClosingHoldModal setOpenHoldModal={setOpenHoldModal} />
    //   </Modal>

    //   {/*   <Box className={"sessioclosing-top-bar"} sx={{ width: "100%" }}>
    //        <div className="session-left-side-wrapper">
    //         <div>
    //           <ChevronLeftIcon width={24} height={22} />
    //         </div>

    //         <div className="session-title-wrapper">
    //           <p className="session-title-p">Sessions</p>
    //           <p className="session-subtitle-p">See all session details</p>
    //         </div>
    //       </div>

    //       <p>
    //         <PosCustomIcon
    //           icon={<XMarkIcon />}
    //           width={24}
    //           height={24}
    //           onClickFunction={(event) => {
    //             event?.stopPropagation?.();
    //             onClose();
    //           }}
    //         />
    //       </p>
    //     </Box> */}

    //   <Box className="session-closing-body" sx={{ width: "100%" }}>
    //     {activeSessions.length > 0 && (
    //       <div className="sesion-closing-current-session-detail">
    //         <section className="sesion-closing-current-session-detail-top">
    //           <p className="session-detail-top-p">Current Session</p>
    //           <PosCustomStatusBatch status="ACTIVE" padding={"2px 10px"} />
    //         </section>

    //         <section className="sesion-closing-current-session-detail-body">
    //           {currentSesionSummary?.map?.((el, idx) => {
    //             return (
    //               <div className="current-session-detail-body-summary-card" key={el?.id || idx}>
    //                 {/* logo */}
    //                 <section
    //                   className="summary-card-logo-section"
    //                   style={{ background: el?.iconParentBg }}
    //                 >
    //                   {el?.icon ?? <></>}
    //                 </section>

    //                 {/* label and value */}
    //                 <section className="summary-card-label-value-section">
    //                   <p className="summary-card-label">{el?.label || "-"}</p>
    //                   <p className="summary-card-value">{el?.value || "-"}</p>
    //                 </section>
    //               </div>
    //             );
    //           })}
    //         </section>
    //       </div>
    //     )}

    //     {/* this is the table for the sessions */}
    //     <div className="sesion-closing-current-sesion-body">
    //       <section className="sesion-closing-current-sesion-body-top-section">
    //         {/* this is the body - header part */}
    //         <p className="current-sesion-body-p">Previous Sessions</p>

    //         {/* this is the table container body */}
    //         <div className="newSessionListingContainer">
    //           <div className="newSessionRightInnerContainer">
    //             {/* <p className="startText">Active Sessions</p> */}
    //             {isSessionLoading ? (
    //               <Box
    //                 sx={{
    //                   display: "flex",
    //                   justifyContent: "center",
    //                   alignItems: "center",
    //                   py: 4,
    //                   flexDirection: "column",
    //                   gap: 1,
    //                 }}
    //               >
    //                 <CircularProgress size={28} sx={{ color: "#2A71FF" }} />
    //                 <Typography sx={{ fontSize: "0.8rem", color: "#888" }}>
    //                   Loading sessions...
    //                 </Typography>
    //               </Box>
    //             ) : sessionTable && !previousDay ? (
    //               <>
    //                 {/* Active Sessions Table */}
    //                 {/* {activeSessions.length > 0 ? (
    //                   <div className="table-scroll-wrapper" style={tableWrapperStyle}>
    //                     <table className="pos-table-main-box-new">
    //                       <thead>
    //                         <tr>
    //                           <th className="table-head-pos">Cashier Name</th>
    //                           <th className="table-head-pos">Billing Counter</th>
    //                           <th className="table-head-pos">License ID</th>
    //                           <th className="table-head-pos">Start Time</th>
    //                           <th className="table-head-pos">End Time</th>
    //                           <th className="table-head-pos">Status</th>
    //                         </tr>
    //                       </thead>
    //                       <tbody>{renderSessionRows(activeSessions)}</tbody>
    //                     </table>
    //                   </div>
    //                 ) : (
    //                   <Typography sx={{ textAlign: "center", py: 2, color: "#666" }}>
    //                     No active sessions
    //                   </Typography>
    //                 )} */}

    //                 {activeSessions.length > 0 ? (
    //                   <CustomerPaginatedTable
    //                     showPagination={false}
    //                     columns={[
    //                       { key: "name", label: "Cashier Name", width: "18%" },
    //                       { key: "licenseName", label: "Billing Counter", width: "20%" },
    //                       { key: "licenseId", label: "License ID", width: "16%" },
    //                       { key: "startTime", label: "Start Time", width: "18%" },
    //                       { key: "endTime", label: "End Time", width: "18%" },
    //                       {
    //                         key: "status",
    //                         label: "Status",
    //                         width: "10%",
    //                         render: (row) => <PosCustomStatusBatch status={row.status} />,
    //                       },
    //                     ]}
    //                     rows={activeSessions.map((item) => ({
    //                       name: item?.name || "-",
    //                       licenseName: item?.licenseName || "-",
    //                       licenseId: item?.licenseId || "-",
    //                       startTime: convertToLocalTime(item?.startTime),
    //                       endTime: item?.endTime ? convertToLocalTime(item?.endTime) : "Ongoing",
    //                       status: item?.status || "-",
    //                       _raw: item,
    //                     }))}
    //                     rowKey="licenseId"
    //                     emptyText="No active sessions"
    //                   />
    //                 ) : (
    //                   <Typography sx={{ textAlign: "center", py: 2, color: "#666" }}>
    //                     No active sessions
    //                   </Typography>
    //                 )}

    //                 {/* Inactive Sessions Accordion */}
    //                 {inactiveSessions.length > 0 && (
    //                   <AccordionDetails sx={{ padding: 0 }}>
    //                     <CustomerPaginatedTable
    //                       showPagination={false}
    //                       columns={[
    //                         { key: "name", label: "Cashier Name", width: "18%" },
    //                         { key: "licenseName", label: "Billing Counter", width: "20%" },
    //                         { key: "licenseId", label: "License ID", width: "16%" },
    //                         { key: "startTime", label: "Start Time", width: "18%" },
    //                         { key: "endTime", label: "End Time", width: "18%" },
    //                         {
    //                           key: "status",
    //                           label: "Status",
    //                           width: "10%",
    //                           render: (row) => <PosCustomStatusBatch status={row.status} />,
    //                         },
    //                       ]}
    //                       rows={inactiveSessions.map((item) => ({
    //                         name: item?.name || "-",
    //                         licenseName: item?.licenseName || "-",
    //                         licenseId: item?.licenseId || "-",
    //                         startTime: convertToLocalTime(item?.startTime),
    //                         endTime: item?.endTime ? convertToLocalTime(item?.endTime) : "Ongoing",
    //                         status: item?.status || "-",
    //                       }))}
    //                       rowKey="licenseId"
    //                       emptyText="No inactive sessions"
    //                     />
    //                   </AccordionDetails>
    //                 )}
    //               </>
    //             ) : (
    //               <PreviousSessionMessage />
    //             )}
    //           </div>

    //           <Drawer
    //             anchor="bottom"
    //             open={drawerOpen}
    //             onClose={toggleDrawer(false)}
    //             sx={{ zIndex: "1201" }}
    //             PaperProps={{
    //               style: {
    //                 backgroundColor: "#ececec",
    //                 borderRadius: "10px",
    //                 margin: !isMobile && "2rem",
    //               },
    //             }}
    //           >
    //             <Box className="summary-main-box">
    //               <div className="summary-title">Session Summary</div>
    //               <SummaryCard data={summaryData} />
    //             </Box>

    //             <Box className="summary-close-button" onClick={toggleDrawer(false)}>
    //               <IconButton aria-label="delete">
    //                 <LockClosedIcon />
    //               </IconButton>
    //             </Box>
    //           </Drawer>
    //           <div className="summary-button-wrapper">
    //             <PosCustomButton
    //               title="Summary"
    //               radius="4px"
    //               p={"12px 16px"}
    //               variant="blue-d"
    //               onClickFunction={toggleDrawer(true)}
    //               iconOnLeft={
    //                 <PosCustomIcon
    //                   icon={<CurrencyRupeeIcon />}
    //                   color="white"
    //                   height={"14px"}
    //                   width={"14px"}
    //                 />
    //               }
    //             />
    //           </div>
    //         </div>
    //       </section>

    //       {/* this is the stop section btn and go to cart btn */}
    //       <div className="newSessionListingButtonContainer">
    //         <PosCustomButton
    //           title="Stop session"
    //           width="50%"
    //           p={"12px 16px"}
    //           radius="4px"
    //           variant="red-transparent"
    //           loading={isHoldCartLoading}
    //           onClickFunction={handleStopDay}
    //         />
    //         <PosCustomButton
    //           title="Go to cart"
    //           width="100%"
    //           p={"12px 16px"}
    //           radius="4px"
    //           variant="blue-d"
    //           onClickFunction={goToCart}
    //           iconOnRight={
    //             <PosCustomIcon
    //               icon={<ArrowLongRightIcon />}
    //               height={"1rem"}
    //               width={"1rem"}
    //               color="var(--surface-white)"
    //             />
    //           }
    //           iconPosition={"corner"}
    //         />
    //       </div>
    //     </div>
    //   </Box>
    // </section>

    <section className="session_closing_home_page-container">
      <style>
        {`
          .table-scroll-wrapper::-webkit-scrollbar { width: 8px; }
          .table-scroll-wrapper::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
          .table-scroll-wrapper::-webkit-scrollbar-thumb { background: #888; border-radius: 4px; }
          .table-scroll-wrapper::-webkit-scrollbar-thumb:hover { background: #555; }
          .session-accordion { margin-top: 12px; box-shadow: none; border: 1px solid #e0e0e0; border-radius: 4px !important; }
          .session-accordion:before { display: none; }
          .accordion-summary { background-color: #f5f5f5; min-height: 48px !important; }
          .accordion-summary .MuiAccordionSummary-content { margin: 8px 0; }
        `}
      </style>

      <Modal
        open={openHoldModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className="modal-popup"
      >
        <ClosingHoldModal setOpenHoldModal={setOpenHoldModal} navigate={navigate} />
      </Modal>

      <Box className="session-closing-body" sx={{ width: "100%" }}>
        {/* ── NEW HEADER ───────────────────────────────────────────── */}
        <div className="session-closing-header">
          <div className="session-closing-header-left">
            <p className="session-closing-header-title">
              <p className="session-closing-header-title-p">Current Session </p>
              <span className="session-status-active-button">
                <PosCustomStatusBatch status="ACTIVE" padding={"3px 12px"} />
              </span>
            </p>
            <p className="session-closing-header-subtitle">Overview of today's active session</p>
          </div>
          <div className="session-closing-header-right">
            <PosCustomIcon
              icon={<XMarkIcon />}
              width={20}
              height={20}
              onClickFunction={onClose}
              pointer={true}
            />
          </div>
        </div>

        {/* ── SUMMARY CARDS (unchanged logic) ─────────────────────── */}
        {!isModalLoading && activeSessions?.length > 0 && (
          <div className="sesion-closing-current-session-detail">
            <section className="sesion-closing-current-session-detail-body">
              {currentSesionSummary?.map?.((el, idx) => (
                <div className="current-session-detail-body-summary-card" key={el?.id || idx}>
                  <section
                    className="summary-card-logo-section"
                    style={{ background: el?.iconParentBg }}
                  >
                    {el?.icon ?? <></>}
                  </section>
                  <section className="summary-card-label-value-section">
                    <p className="summary-card-label">{el?.label || "-"}</p>
                    <p className="summary-card-value">{el?.value || "-"}</p>
                  </section>
                </div>
              ))}
            </section>
          </div>
        )}

        {/* ── SESSION TABLE SECTION (zero logic changes) ───────────── */}
        <div className="sesion-closing-current-sesion-body">
          <section className="sesion-closing-current-sesion-body-top-section">
            <p className="current-sesion-body-p">Previous Sessions</p>

            <div className="newSessionListingContainer">
              <div className="newSessionRightInnerContainer">
                {isModalLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 4,
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={28} sx={{ color: "#2A71FF" }} />
                    <Typography sx={{ fontSize: "0.8rem", color: "#888" }}>
                      Loading sessions...
                    </Typography>
                  </Box>
                ) : sessionTable && !previousDay ? (
                  <>
                    {/* Active Sessions Table */}
                    {activeSessions.length > 0 ? (
                      <CustomerPaginatedTable
                        showPagination={false}
                        columns={[
                          { key: "name", label: "Cashier Name", width: "18%" },
                          { key: "licenseName", label: "Billing Counter", width: "20%" },
                          { key: "licenseId", label: "License ID", width: "16%" },
                          { key: "startTime", label: "Start Time", width: "18%" },
                          { key: "endTime", label: "End Time", width: "18%" },
                          {
                            key: "status",
                            label: "Status",
                            width: "10%",
                            render: (row) => <PosCustomStatusBatch status={row.status} />,
                          },
                        ]}
                        rows={activeSessions.map((item) => ({
                          name: item?.name || "-",
                          licenseName: item?.licenseName || "-",
                          licenseId: item?.licenseId || "-",
                          startTime: convertToLocalTime(item?.startTime),
                          endTime: item?.endTime ? convertToLocalTime(item?.endTime) : "Ongoing",
                          status: item?.status || "-",
                          _raw: item,
                        }))}
                        rowKey="licenseId"
                        emptyText="No active sessions"
                      />
                    ) : (
                      <Typography sx={{ textAlign: "center", py: 2, color: "#666" }}>
                        No active sessions
                      </Typography>
                    )}

                    {/* Inactive Sessions Accordion */}
                    {inactiveSessions.length > 0 && (
                      <Accordion
                        className="session-accordion"
                        expanded={accordionExpanded}
                        onChange={() => setAccordionExpanded(!accordionExpanded)}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          className="accordion-summary"
                        >
                          <Typography sx={{ fontWeight: 500 }}>
                            Other Sessions ({inactiveSessions?.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ padding: 0 }}>
                          <CustomerPaginatedTable
                            showPagination={false}
                            columns={[
                              { key: "name", label: "Cashier Name", width: "18%" },
                              { key: "licenseName", label: "Billing Counter", width: "20%" },
                              { key: "licenseId", label: "License ID", width: "16%" },
                              { key: "startTime", label: "Start Time", width: "18%" },
                              { key: "endTime", label: "End Time", width: "18%" },
                              {
                                key: "status",
                                label: "Status",
                                width: "10%",
                                render: (row) => <PosCustomStatusBatch status={row.status} />,
                              },
                            ]}
                            rows={inactiveSessions.map((item) => ({
                              name: item?.name || "-",
                              licenseName: item?.licenseName || "-",
                              licenseId: item?.licenseId || "-",
                              startTime: convertToLocalTime(item?.startTime),
                              endTime: item?.endTime
                                ? convertToLocalTime(item?.endTime)
                                : "Ongoing",
                              status: item?.status || "-",
                            }))}
                            rowKey="licenseId"
                            emptyText="No inactive sessions"
                          />
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </>
                ) : null}
              </div>

              {/* Summary drawer — untouched */}
              <Drawer
                anchor="bottom"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
                sx={{ zIndex: "1201" }}
                PaperProps={{
                  style: {
                    backgroundColor: "#ececec",
                    borderRadius: "10px",
                    margin: !isMobile && "2rem",
                  },
                }}
              >
                <Box className="summary-main-box">
                  <div className="summary-title">Session Summary</div>
                  <SummaryCard data={summaryData} />
                </Box>
                <Box className="summary-close-button" onClick={toggleDrawer(false)}>
                  <IconButton aria-label="delete">
                    <LockClosedIcon />
                  </IconButton>
                </Box>
              </Drawer>

              {/* Summary button — untouched */}
              {/* <div className="summary-button-wrapper">
                <PosCustomButton
                  title="Summary"
                  radius="4px"
                  p={"12px 16px"}
                  variant="blue-d"
                  onClickFunction={toggleDrawer(true)}
                  iconOnLeft={
                    <PosCustomIcon
                      icon={<CurrencyRupeeIcon />}
                      color="white"
                      height={"14px"}
                      width={"14px"}
                    />
                  }
                />
              </div> */}
            </div>
          </section>

          {/* ── NEW CTA CARD (replaces old Stop/Go-to-cart buttons) ── */}
          <div className="session-close-cta-card">
            <p className="session-close-cta-title">Ready to close for the day?</p>
            <p className="session-close-cta-subtitle">
              This will trigger the end-of-day reconciliation process. You will need manager
              approval to proceed.
            </p>

            <PosCustomButton
              title={"Close Session"}
              iconOnRight={
                <PosCustomIcon icon={<LogoutIcon />} height={"16px"} width={"16px"} color="white" />
              }
              p={" 12px 28px"}
              radius={"15px"}
              variant={"custom-variant"}
              customBackgroundColor={"#e8003d"}
              customColor={"#fff"}
              customBorder={"none"}
              // onClickFunction={handleStopDay}
              onClickFunction={holdCartCheckFunction}
            />
          </div>
        </div>
      </Box>
    </section>

    // </Drawer>
  );
};

export default SessionClosingDrawer;
